// ---------------------------------------------------------------------------
// BrandsTeamsPanel -- Enhanced dual-column brand and team affinity
// ---------------------------------------------------------------------------
// Features:
// - Brands: category distribution donut, subcategory badges, gradient bars
// - Teams: toggle Teams/Players, city display, country tags
// ---------------------------------------------------------------------------

import React, { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { ShoppingBag, Trophy, ChevronDown, User, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { BrandAffinity, TeamLoyalty } from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BrandsTeamsPanelProps {
  readonly brands: readonly BrandAffinity[];
  readonly teams: readonly TeamLoyalty[];
  readonly totalProfiles?: number;
  readonly hasMoreBrands: boolean;
  readonly hasMoreTeams: boolean;
  readonly loadingBrands: boolean;
  readonly loadingTeams: boolean;
  readonly onLoadMoreBrands: () => void;
  readonly onLoadMoreTeams: () => void;
  readonly className?: string;
}

type TeamFilter = 'all' | 'team' | 'player';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORY_COLORS: Readonly<Record<string, string>> = {
  'fashion & apparel': '#EC4899',
  fashion: '#EC4899',
  apparel: '#EC4899',
  sports: '#3B82F6',
  'automotive & transportation': '#EF4444',
  automotive: '#EF4444',
  electronics: '#8B5CF6',
  technology: '#8B5CF6',
  'beauty & cosmetics': '#F59E0B',
  'food & beverages': '#10B981',
  food: '#10B981',
  beverages: '#10B981',
  'gambling & betting': '#78716c',
  'financial services': '#16A34A',
  finance: '#16A34A',
  'retail & e-commerce': '#F97316',
  retail: '#F97316',
  'media & entertainment': '#A855F7',
  media: '#A855F7',
  entertainment: '#A855F7',
  telecommunications: '#0EA5E9',
  'travel & hospitality': '#14B8A6',
  'health & wellness': '#22C55E',
  'energy & utilities': '#EAB308',
};

const TEAM_FILTER_PILLS: readonly { readonly id: TeamFilter; readonly label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'team', label: 'Teams' },
  { id: 'player', label: 'Players' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function BrandsTeamsPanel({
  brands,
  teams,
  totalProfiles = 0,
  hasMoreBrands,
  hasMoreTeams,
  loadingBrands,
  loadingTeams,
  onLoadMoreBrands,
  onLoadMoreTeams,
  className,
}: BrandsTeamsPanelProps) {
  const [teamFilter, setTeamFilter] = useState<TeamFilter>('all');
  const [brandCategoryFilter, setBrandCategoryFilter] = useState<string | null>(null);

  // Category distribution for donut chart
  const categoryDistribution = useMemo(() => {
    const map = new Map<string, number>();
    for (const brand of brands) {
      const cat = brand.brand_category_name ?? 'Other';
      map.set(cat, (map.get(cat) ?? 0) + brand.profiles_count);
    }
    return [...map.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [brands]);

  // Top category pills (max 5 + "All")
  const categoryPills = useMemo(() => {
    return categoryDistribution.slice(0, 5).map(c => c.name);
  }, [categoryDistribution]);

  // Filter brands by category
  const filteredBrands = useMemo(() => {
    if (!brandCategoryFilter) return brands;
    return brands.filter(b => (b.brand_category_name ?? 'Other') === brandCategoryFilter);
  }, [brands, brandCategoryFilter]);

  const maxBrandCount = filteredBrands[0]?.profiles_count ?? 1;

  // Filter teams by type
  const filteredTeams = useMemo(() => {
    if (teamFilter === 'all') return teams;
    return teams.filter(t => t.type === teamFilter);
  }, [teams, teamFilter]);

  const maxTeamCount = filteredTeams[0]?.profiles_count ?? 1;

  // Count teams vs players
  const teamCount = useMemo(() => teams.filter(t => t.type === 'team').length, [teams]);
  const playerCount = useMemo(() => teams.filter(t => t.type === 'player').length, [teams]);

  // Subcategory distribution (only when a category is active)
  const subcategoryDistribution = useMemo(() => {
    if (!brandCategoryFilter) return [];
    const map = new Map<string, number>();
    for (const brand of filteredBrands) {
      const sub = brand.brand_subcategory_name ?? 'Other';
      map.set(sub, (map.get(sub) ?? 0) + brand.profiles_count);
    }
    return [...map.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredBrands, brandCategoryFilter]);

  const handleDonutClick = (categoryName: string) => {
    setBrandCategoryFilter(prev => (prev === categoryName ? null : categoryName));
  };

  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2 gap-6', className)}>
      {/* ── Brands Column ── */}
      <div className="space-y-4">
        {/* Category Distribution Donut */}
        {categoryDistribution.length > 1 && (
          <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
            <div className="text-sm font-semibold text-v11-text-primary mb-4">
              Category Distribution
            </div>
            <CategoryDonut
              categories={categoryDistribution}
              activeCategory={brandCategoryFilter}
              onCategoryClick={handleDonutClick}
            />
          </div>
        )}

        {/* Subcategory Breakdown (when category filter active) */}
        {brandCategoryFilter && subcategoryDistribution.length > 1 && (
          <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
            <div className="text-sm font-semibold text-v11-text-primary mb-3">
              {brandCategoryFilter} — Subcategories
            </div>
            <SubcategoryBreakdown
              subcategories={subcategoryDistribution}
              categoryColor={CATEGORY_COLORS[brandCategoryFilter.toLowerCase()] ?? '#64748b'}
            />
          </div>
        )}

        {/* Brand List */}
        <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-orange-400" />
              <span className="text-base font-semibold text-v11-text-primary">Brand Affinity</span>
              <span className="text-xs text-v11-text-muted">({filteredBrands.length})</span>
            </div>
          </div>
          {/* Category filter pills */}
          {categoryPills.length > 1 && (
            <div className="flex flex-wrap items-center gap-1 mb-4">
              <button
                onClick={() => setBrandCategoryFilter(null)}
                className={cn(
                  'text-[11px] px-2.5 py-1 rounded-full font-medium transition-all',
                  !brandCategoryFilter
                    ? 'bg-orange-500/15 text-orange-400 ring-1 ring-orange-500/30'
                    : 'text-v11-text-muted hover:text-v11-text-secondary hover:bg-v11-bg'
                )}
              >
                All
              </button>
              {categoryPills.map(cat => {
                const color = CATEGORY_COLORS[cat.toLowerCase()] ?? '#64748b';
                const isActive = brandCategoryFilter === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setBrandCategoryFilter(isActive ? null : cat)}
                    className={cn(
                      'text-[11px] px-2.5 py-1 rounded-full font-medium transition-all',
                      isActive
                        ? 'ring-1'
                        : 'text-v11-text-muted hover:text-v11-text-secondary hover:bg-v11-bg'
                    )}
                    style={
                      isActive
                        ? { backgroundColor: `${color}20`, color, borderColor: `${color}50` }
                        : undefined
                    }
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          )}

          <div className="space-y-3">
            {filteredBrands.map((brand, i) => {
              const pct =
                totalProfiles > 0
                  ? ((brand.profiles_count / totalProfiles) * 100).toFixed(1)
                  : null;
              const barWidth = (brand.profiles_count / maxBrandCount) * 100;
              const catColor = brand.brand_category_name
                ? (CATEGORY_COLORS[brand.brand_category_name.toLowerCase()] ?? '#64748b')
                : '#64748b';

              return (
                <div key={brand.brand_id}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs text-v11-text-muted w-5 text-right font-semibold">
                        {i + 1}
                      </span>
                      <span className="text-sm text-v11-text-primary truncate font-medium">
                        {brand.brand_name}
                      </span>
                      {brand.brand_category_name && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0"
                          style={{ backgroundColor: `${catColor}20`, color: catColor }}
                        >
                          {brand.brand_category_name}
                        </span>
                      )}
                      {brand.brand_subcategory_name && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-v11-bg text-v11-text-muted font-medium flex-shrink-0">
                          {brand.brand_subcategory_name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      {pct && (
                        <span className="text-[10px] text-v11-text-muted tabular-nums">{pct}%</span>
                      )}
                      <span className="text-xs font-semibold text-v11-text-secondary tabular-nums">
                        {brand.profiles_count.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="ml-7 h-1.5 bg-v11-bg rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${barWidth}%`,
                        background: `linear-gradient(90deg, ${catColor}, ${catColor}99)`,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {hasMoreBrands && (
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={onLoadMoreBrands}
                disabled={loadingBrands}
                className="border-v11-border text-v11-text-secondary hover:bg-v11-surface-hover"
              >
                {loadingBrands ? 'Loading...' : 'Load More'}
                <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ── Teams Column ── */}
      <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-emerald-400" />
            <span className="text-base font-semibold text-v11-text-primary">Team Loyalty</span>
            <span className="text-xs text-v11-text-muted">({filteredTeams.length})</span>
          </div>
          {/* Filter pills */}
          <div className="flex items-center gap-1">
            {TEAM_FILTER_PILLS.map(pill => (
              <button
                key={pill.id}
                onClick={() => setTeamFilter(pill.id)}
                className={cn(
                  'text-[11px] px-2.5 py-1 rounded-full font-medium transition-all',
                  teamFilter === pill.id
                    ? 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30'
                    : 'text-v11-text-muted hover:text-v11-text-secondary hover:bg-v11-bg'
                )}
              >
                {pill.label}
                {pill.id === 'team' && teamCount > 0 && (
                  <span className="ml-1 text-[9px] opacity-60">{teamCount}</span>
                )}
                {pill.id === 'player' && playerCount > 0 && (
                  <span className="ml-1 text-[9px] opacity-60">{playerCount}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {filteredTeams.map((team, i) => {
            const pct =
              totalProfiles > 0 ? ((team.profiles_count / totalProfiles) * 100).toFixed(1) : null;
            const barWidth = (team.profiles_count / maxTeamCount) * 100;
            const isPlayer = team.type === 'player';

            return (
              <div key={`${team.type}-${team.entity_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-v11-text-muted w-5 text-right font-semibold">
                      {i + 1}
                    </span>
                    {/* Type icon */}
                    {isPlayer ? (
                      <User className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />
                    ) : (
                      <Shield className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />
                    )}
                    <span className="text-sm text-v11-text-primary truncate font-medium">
                      {team.team_name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    {pct && (
                      <span className="text-[10px] text-v11-text-muted tabular-nums">{pct}%</span>
                    )}
                    <span className="text-xs font-semibold text-v11-text-secondary tabular-nums">
                      {team.profiles_count.toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="ml-7 h-1.5 bg-v11-bg rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${barWidth}%`,
                      background: isPlayer
                        ? 'linear-gradient(90deg, #f59e0b, #fbbf24)'
                        : 'linear-gradient(90deg, #10b981, #34d399)',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {hasMoreTeams && (
          <div className="flex justify-center pt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={onLoadMoreTeams}
              disabled={loadingTeams}
              className="border-v11-border text-v11-text-secondary hover:bg-v11-surface-hover"
            >
              {loadingTeams ? 'Loading...' : 'Load More'}
              <ChevronDown className="h-3 w-3 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SubcategoryBreakdown — horizontal bars for subcategory distribution
// ---------------------------------------------------------------------------

function SubcategoryBreakdown({
  subcategories,
  categoryColor,
}: {
  readonly subcategories: readonly { name: string; count: number }[];
  readonly categoryColor: string;
}) {
  const total = subcategories.reduce((sum, s) => sum + s.count, 0);
  if (total === 0) return null;

  const maxCount = subcategories[0]?.count ?? 1;

  return (
    <div>
      <div className="space-y-2">
        {subcategories.slice(0, 6).map((sub, i) => {
          const pct = (sub.count / total) * 100;
          const barWidth = (sub.count / maxCount) * 100;
          const opacity = 1 - i * 0.12;

          return (
            <div key={sub.name} className="flex items-center gap-2">
              <span className="text-xs text-v11-text-secondary w-28 truncate">{sub.name}</span>
              <div className="flex-1 h-2 rounded-full bg-v11-bg overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${barWidth}%`,
                    backgroundColor: categoryColor,
                    opacity,
                  }}
                />
              </div>
              <span className="text-[10px] text-v11-text-muted tabular-nums w-10 text-right">
                {pct.toFixed(0)}%
              </span>
            </div>
          );
        })}
        {subcategories.length > 6 && (
          <div className="text-[10px] text-v11-text-muted pl-[120px]">
            +{subcategories.length - 6} more
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CategoryDonut — SVG donut chart for brand category distribution
// ---------------------------------------------------------------------------

function CategoryDonut({
  categories,
  activeCategory,
  onCategoryClick,
}: {
  readonly categories: readonly { name: string; count: number }[];
  readonly activeCategory: string | null;
  readonly onCategoryClick: (name: string) => void;
}) {
  const total = categories.reduce((sum, c) => sum + c.count, 0);
  if (total === 0) return null;

  const SIZE = 140;
  const STROKE = 24;
  const RADIUS = (SIZE - STROKE) / 2;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  // Build arcs
  const arcs: { offset: number; length: number; color: string; name: string; pct: number }[] = [];
  let currentOffset = 0;

  for (const cat of categories.slice(0, 8)) {
    const pct = cat.count / total;
    const length = pct * CIRCUMFERENCE;
    const color = CATEGORY_COLORS[cat.name.toLowerCase()] ?? '#64748b';
    arcs.push({ offset: currentOffset, length, color, name: cat.name, pct: pct * 100 });
    currentOffset += length;
  }

  // If there are more, add "Other"
  if (categories.length > 8) {
    const otherPct = categories.slice(8).reduce((s, c) => s + c.count, 0) / total;
    arcs.push({
      offset: currentOffset,
      length: otherPct * CIRCUMFERENCE,
      color: '#475569',
      name: 'Other',
      pct: otherPct * 100,
    });
  }

  return (
    <div className="flex items-center gap-5">
      {/* SVG Donut */}
      <div className="flex-shrink-0">
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
          {arcs.map((arc, i) => {
            const isActive = activeCategory === arc.name;
            const isDimmed = activeCategory && !isActive;
            return (
              <circle
                key={i}
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke={arc.color}
                strokeWidth={isActive ? STROKE + 4 : STROKE}
                strokeDasharray={`${arc.length} ${CIRCUMFERENCE - arc.length}`}
                strokeDashoffset={-arc.offset}
                strokeLinecap="butt"
                transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
                className="transition-all duration-300 cursor-pointer"
                style={{ opacity: isDimmed ? 0.3 : 1 }}
                onClick={() => onCategoryClick(arc.name)}
              />
            );
          })}
          {/* Center text */}
          <text
            x={SIZE / 2}
            y={SIZE / 2 - 4}
            textAnchor="middle"
            className="fill-v11-text-primary text-lg font-bold"
            style={{ fontSize: '18px', fontWeight: 700 }}
          >
            {categories.length}
          </text>
          <text
            x={SIZE / 2}
            y={SIZE / 2 + 12}
            textAnchor="middle"
            className="fill-v11-text-muted"
            style={{ fontSize: '10px' }}
          >
            categories
          </text>
        </svg>
      </div>

      {/* Legend */}
      <div className="flex-1 space-y-1.5">
        {arcs.slice(0, 6).map(arc => {
          const isActive = activeCategory === arc.name;
          const isDimmed = activeCategory && !isActive;
          return (
            <button
              key={arc.name}
              onClick={() => onCategoryClick(arc.name)}
              className={cn(
                'flex items-center gap-2 w-full text-left transition-opacity cursor-pointer',
                isDimmed ? 'opacity-30' : 'opacity-100'
              )}
            >
              <div
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={{ backgroundColor: arc.color }}
              />
              <span
                className={cn(
                  'text-xs truncate flex-1',
                  isActive ? 'text-v11-text-primary font-medium' : 'text-v11-text-secondary'
                )}
              >
                {arc.name}
              </span>
              <span className="text-[10px] text-v11-text-muted tabular-nums flex-shrink-0">
                {arc.pct.toFixed(0)}%
              </span>
            </button>
          );
        })}
        {arcs.length > 6 && (
          <div className="text-[10px] text-v11-text-muted">+{arcs.length - 6} more</div>
        )}
      </div>
    </div>
  );
}

export { BrandsTeamsPanel };
export type { BrandsTeamsPanelProps };
